import { AppError } from "@shared/app-error";
import { type Accessor, createContext, type ParentProps, useContext } from "solid-js";
import { type AcquireReleaseState, createAcquireRelease } from "./create-acquire-release";

/**
 * §4.7.1 / §5.2「リソース管理規律」: カメラの MediaStream を保持する Provider。
 *
 * 重要な実装上の制約:
 * - getUserMedia は user activation 内 (ボタンの click ハンドラの最初の同期パス) で
 *   呼ばなければならない。 createResource は signal 評価で起動するためタイミングを
 *   外しやすい。 そこで明示的な `acquire()` 関数を context に出して、
 *   呼び出し側でクリックハンドラ内から同期的に呼べるようにする
 * - `getUserMedia({ video: { frameRate: { ideal: 15 } } })` を 1 回だけ呼ぶ。 audio constraint は
 *   issue #130 で撤去済 (= mic permission ダイアログを出さない、 vision-VAD で発話判定するため
 *   audio track は不要)。 frameRate の ideal ヒントは issue #260: 検知は `frame-loop` の
 *   `PROCESSING_CAP_FPS=15` throttle で ~15fps に絞っているため、 カメラが 30fps を出すと半分の
 *   capture フレームを捨てる。 ideal:15 で capture 側も抑え、 捨てフレームのセンサ/ISP/帯域コストを
 *   削る (実機 C920 で 30→15 着地を確認)
 * - 取得済み + active なら `acquire()` 再呼び出しでも同じ stream を返す。 inactive
 *   (= track が外部要因で停止) なら作り直す
 * - Provider が unmount された時点で MediaStream の全 track を stop する
 *
 * race / state machine は createAcquireRelease に集約。 ここでは I/O 境界 (getUserMedia)
 * と DOM Exception → AppError の正規化のみを担当する。
 */

export type CameraContextValue = {
  stream: Accessor<MediaStream | null>;
  state: Accessor<AcquireReleaseState<MediaStream>>;
  /**
   * クリックハンドラから同期的に呼ぶこと。 await や事前 permission query を挟むと
   * user activation を消費して許可ダイアログが出ない (§4.7.1)。
   */
  acquire: () => Promise<MediaStream>;
  /**
   * §issue #101 — camera は fresh-per-handoff: handoff のたびに stream を release し
   * fresh `getUserMedia` で取り直す。 CameraProvider scope (app-layout) は消費 page より
   * 長生きするため scope-death では破棄されない。 消費者 (session-page / permission-denied-page)
   * と `startSessionFlow` がこの `release()` を明示的に呼んで per-handoff lifecycle を管理する。
   */
  release: () => void;
};

const CameraContext = createContext<CameraContextValue | null>(null);

export function CameraProvider(props: ParentProps) {
  const handle = createAcquireRelease<MediaStream>({
    // user activation を保つため、 await を挟まずこの行で getUserMedia を起動する。
    // issue #130: vision-VAD 統一で audio constraint を撤去 (= mic permission を要求しない)。
    // issue #260: frameRate は **ideal** のみ指定する。 exact/max はハード非対応で
    // OverconstrainedError → `CameraStreamLost` の fatal (下記 toAppError) を踏みセッションを落とす。
    // ideal は best-effort でこの経路を踏まない (= 着地しなくても害なく、 frame-loop throttle が最終調整)。
    // 下振れ時の検知中立は F34 (issue #232 / `docs/eval-optimization-progress.md`) が floor ~8fps で裏付け。
    init: () => navigator.mediaDevices.getUserMedia({ video: { frameRate: { ideal: 15 } } }),
    dispose: (stream) => {
      for (const track of stream.getTracks()) track.stop();
    },
    canReuse: (s) => s.active,
    toAppError,
    // §AppError policy: `CameraStreamLost` は 3 policy で使い分ける設計:
    //   - transient (本 abortedCode 経路): teardown 中の release race。 caller は `signal.aborted`
    //     で握り潰す前提なので toast は出さない (session-page handleStart の catch path 参照)
    //   - fallback: 実 I/O の `NotReadableError` (= 別タブが奪ったケース) で別経路に切替
    //   - fatal: `NotFoundError` / `OverconstrainedError` (デバイス無し / 制約違反)
    // 同 code の理由: handle-app-error の messageFor は code 単位なので、 user 向けは
    // 「カメラの接続が切れました」 で統一しつつ、 catcher 側の挙動だけ policy で分岐させる。
    abortedCode: "CameraStreamLost",
  });

  return (
    <CameraContext.Provider
      value={{
        stream: handle.resource,
        state: handle.state,
        acquire: handle.acquire,
        release: handle.release,
      }}
    >
      {props.children}
    </CameraContext.Provider>
  );
}

export function useCamera(): CameraContextValue {
  const value = useContext(CameraContext);
  if (!value) throw new Error("useCamera must be called inside <CameraProvider>");
  return value;
}

function toAppError(err: unknown): AppError {
  if (!(err instanceof DOMException)) {
    return new AppError("Unexpected", "fatal", err instanceof Error ? err.message : String(err), err);
  }
  // §UC-01-D: NotAllowedError はカメラの許可拒否 (issue #130 以後は mic 経路が無いので camera 単独)。
  switch (err.name) {
    case "NotAllowedError":
    case "SecurityError":
      return new AppError("CameraDenied", "fatal", err.message, err);
    case "NotFoundError":
    case "OverconstrainedError":
      return new AppError("CameraStreamLost", "fatal", err.message, err);
    case "NotReadableError":
      return new AppError("CameraStreamLost", "fallback", err.message, err);
    default:
      return new AppError("Unexpected", "fatal", err.message, err);
  }
}
